import { ImageIcon, X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';

import { uploadBlogImage } from '../../../services/blogAssetService';
import { resolveBlogImageSrc } from '../../../config/blogImagePaths';

export interface MetaValues {
  title: string;
  slug: string;
  meta_title: string;
  meta_description: string;
  keywords: string[];
  featured_image: string;
  author_name: string;
  category: string;
  read_time_min: number | '';
}

interface Props {
  values: MetaValues;
  postId: string | null;
  onChange: (patch: Partial<MetaValues>) => void;
  onGenerateImages?: () => void;
  generatingImages?: boolean;
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 120);
}

const inputCls = 'ds-input';

const Field: React.FC<{ label: string; children: React.ReactNode; hint?: string }> = ({
  label, children, hint,
}) => (
  <div className="flex flex-col gap-1">
    <label className="t-label">{label}</label>
    {children}
    {hint && <p className="t-meta">{hint}</p>}
  </div>
);

const Input: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = (props) => (
  <input
    {...props}
    className={`${inputCls} ${props.className ?? ''}`}
  />
);


export const BlogMetaForm: React.FC<Props> = ({
  values,
  postId,
  onChange,
  onGenerateImages,
  generatingImages = false,
}) => {
  const [kwInput, setKwInput] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState<string | null>(null);
  const [imageBroken, setImageBroken] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setImageBroken(false);
  }, [values.featured_image, values.slug]);

  const [slugManual, setSlugManual] = useState(false);

  const handleTitle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const title = e.target.value;
    const patch: Partial<MetaValues> = { title };
    if (!slugManual) patch.slug = slugify(title);
    onChange(patch);
  };

  const handleSlug = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSlugManual(true);
    onChange({ slug: slugify(e.target.value) });
  };

  const addKeyword = () => {
    const kw = kwInput.trim().toLowerCase();
    if (kw && !values.keywords.includes(kw)) {
      onChange({ keywords: [...values.keywords, kw] });
    }
    setKwInput('');
  };

  const removeKeyword = (kw: string) =>
    onChange({ keywords: values.keywords.filter((k) => k !== kw) });

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadErr(null);
    setUploading(true);
    try {
      const id = postId ?? 'tmp-' + crypto.randomUUID();
      const { publicUrl } = await uploadBlogImage(file, id);
      onChange({ featured_image: publicUrl });
    } catch (err) {
      setUploadErr((err as Error).message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
      {/* Title */}
      <div className="md:col-span-2">
        <Field label="Title *">
          <Input
            value={values.title}
            onChange={handleTitle}
            placeholder="How to land your first job in 30 days"
            maxLength={200}
          />
        </Field>
      </div>

      {/* Slug */}
      <Field label="Slug *" hint="auto-generated from title — only a-z 0-9 hyphens">
        <Input
          value={values.slug}
          onChange={handleSlug}
          placeholder="how-to-land-your-first-job"
          maxLength={120}
        />
      </Field>

      {/* Author */}
      <Field label="Author name">
        <Input
          value={values.author_name}
          onChange={(e) => onChange({ author_name: e.target.value })}
          placeholder="GoGrabJob Team"
        />
      </Field>

      {/* Meta title */}
      <Field label="Meta title" hint="defaults to post title if blank">
        <Input
          value={values.meta_title}
          onChange={(e) => onChange({ meta_title: e.target.value })}
          placeholder="How to land your first job | GoGrabJob"
          maxLength={120}
        />
      </Field>

      {/* Category */}
      <Field label="Category">
        <Input
          value={values.category}
          onChange={(e) => onChange({ category: e.target.value })}
          placeholder="Career Advice"
        />
      </Field>

      {/* Meta description */}
      <div className="md:col-span-2">
        <Field label="Meta description" hint="recommended 150–160 chars">
          <textarea
            value={values.meta_description}
            onChange={(e) => onChange({ meta_description: e.target.value })}
            rows={2}
            maxLength={300}
            placeholder="A concise summary shown in search engine results…"
            className="ds-textarea resize-none"
          />
          <p className="t-meta t-num text-right mt-0.5">
            {values.meta_description.length}/300
          </p>
        </Field>
      </div>

      {/* Keywords */}
      <div className="md:col-span-2">
        <Field label="Keywords">
          <div className="flex gap-2">
            <Input
              value={kwInput}
              onChange={(e) => setKwInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addKeyword(); } }}
              placeholder="Type a keyword and press Enter"
            />
            <button
              type="button"
              onClick={addKeyword}
              className="ds-btn ds-btn-primary whitespace-nowrap min-h-[44px] touch-manipulation"
            >
              Add
            </button>
          </div>
          {values.keywords.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {values.keywords.map((kw) => (
                <span
                  key={kw}
                  className="ds-chip ds-chip-brand"
                >
                  {kw}
                  <button
                    type="button"
                    onClick={() => removeKeyword(kw)}
                    className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] -mr-2 hover:text-[var(--ds-danger)] touch-manipulation"
                    aria-label={`Remove keyword ${kw}`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </Field>
      </div>

      {/* Read time + Featured image */}
      <Field label="Read time (min)">
        <Input
          type="number"
          min={1}
          max={60}
          value={values.read_time_min}
          onChange={(e) =>
            onChange({ read_time_min: e.target.value ? Number(e.target.value) : '' })
          }
          placeholder="5"
        />
      </Field>

      <Field label="Featured image">
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="ds-btn ds-btn-secondary disabled:opacity-50"
          >
            <ImageIcon className="w-4 h-4" />
            {uploading ? 'Uploading…' : 'Choose image'}
          </button>
          {onGenerateImages && (
            <button
              type="button"
              onClick={onGenerateImages}
              disabled={generatingImages || !postId}
              className="ds-btn ds-btn-primary disabled:opacity-50"
              title={postId ? 'Generate hero image from post content' : 'Save draft first'}
            >
              {generatingImages ? 'Generating…' : 'Generate hero image'}
            </button>
          )}
          {values.featured_image && !imageBroken && (
            <img
              key={values.featured_image || values.slug}
              src={resolveBlogImageSrc(values.featured_image, values.slug) ?? values.featured_image}
              alt="featured"
              className="h-10 w-16 object-cover rounded border"
              style={{ borderColor: 'var(--ds-border)' }}
              onError={() => setImageBroken(true)}
            />
          )}
          {values.featured_image && imageBroken && (
            <span className="t-meta text-xs px-2 py-1 rounded border border-dashed" style={{ borderColor: 'var(--ds-border)' }}>
              Image pending
            </span>
          )}
          {values.featured_image && (
            <button
              type="button"
              onClick={() => onChange({ featured_image: '' })}
              className="ds-btn ds-btn-ghost ds-btn-sm p-0 hover:text-[var(--ds-danger)]"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        {uploadErr && <p className="t-meta t-danger mt-1">{uploadErr}</p>}
        {onGenerateImages && (
          <p className="t-meta mt-1">
            Save the draft, then click <strong>Generate hero image</strong>.
            {import.meta.env.DEV
              ? ' Saves to public/images/blogs/<slug>.png and updates this post.'
              : ' Uploads to Supabase storage and updates this post.'}
          </p>
        )}
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleImageUpload}
        />
      </Field>
    </div>
  );
};
